import { Img } from 'openimg/react'
import { useRef } from 'react'
import { Link, Form } from 'react-router'
import { useTranslation } from '#app/utils/i18n.ts'
import { cn, getUserImgSrc } from '#app/utils/misc.tsx'
import { useUser } from '#app/utils/user.ts'
import { Button } from './ui/button'
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuPortal,
	DropdownMenuContent,
	DropdownMenuItem,
} from './ui/dropdown-menu'
import { Icon } from './ui/icon'

/**
 * `hideNameBelowSm` — opt-in, default `false`: with it the visible name drops
 * below `sm` and moves into the control's `aria-label`, so nothing is lost to a
 * screen reader. Off by default on purpose — the default rendering has to stay
 * byte-identical for the callers that do not ask for it.
 */
export function UserDropdown({
	hideNameBelowSm = false,
}: { hideNameBelowSm?: boolean } = {}) {
	const user = useUser()
	const t = useTranslation()
	const formRef = useRef<HTMLFormElement>(null)
	const displayName = user.name ?? user.username
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button asChild variant="secondary">
					<Link
						to={`/users/${user.username}`}
						// this is for progressive enhancement
						onClick={(e) => e.preventDefault()}
						className="flex items-center gap-2"
						aria-label={
							hideNameBelowSm
								? `${t('userMenu.label')}: ${displayName}`
								: t('userMenu.label')
						}
					>
						<Img
							className="size-8 rounded-full object-cover"
							alt={displayName}
							src={getUserImgSrc(user.image?.objectKey)}
							width={256}
							height={256}
							aria-hidden="true"
						/>
						<span
							className={cn(
								'text-body-sm font-bold',
								hideNameBelowSm && 'hidden sm:inline',
							)}
						>
							{displayName}
						</span>
					</Link>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="end">
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={`/users/${user.username}`}>
							<Icon className="text-body-md" name="avatar">
								{t('userMenu.profile')}
							</Icon>
						</Link>
					</DropdownMenuItem>
					<Form action="/logout" method="POST" ref={formRef}>
						<DropdownMenuItem asChild>
							<button type="submit" className="w-full">
								<Icon className="text-body-md" name="exit">
									{t('userMenu.logout')}
								</Icon>
							</button>
						</DropdownMenuItem>
					</Form>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	)
}
